import { useMemo, useState } from "react";
import { Calendar, MapPin, MessageCircle, Users, Wallet, X } from "lucide-react";

import { Badge, Button, PlayerPicker, Sheet, TournamentStatusBadge, useConfirm } from "@/ui";
import { BracketView } from "@/features/events/BracketView";
import { TournamentStandings } from "@/features/tournaments/TournamentStandings";
import { formatEventDate, formatPrice } from "@/lib/format";
import { noAutofill } from "@/lib/noAutofill";
import {
  useAcceptInvite,
  useBracketTeams,
  useCancelInvite,
  useDeclineInvite,
  useInvitePartner,
  searchPlayers,
  useRegisterTournament,
  useRegisterTraining,
  useTournament,
  useTournamentInvites,
  useTraining,
  useUnregisterTournament,
  useUnregisterTraining,
  type TournamentInvite,
} from "@/features/queries";
import { useAuth } from "@/contexts/AuthContext";
import type { EventCardData } from "./EventCard";

interface Props {
  event: EventCardData | null;
  onClose: () => void;
}

export function EventDetailSheet({ event, onClose }: Props) {
  const open = event !== null;

  return (
    <Sheet open={open} onClose={onClose}>
      {event?.kind === "tournament" && <TournamentDetail tournamentId={event.data.id} />}
      {event?.kind === "training" && <TrainingDetail trainingId={event.data.id} />}
    </Sheet>
  );
}

function TournamentDetail({ tournamentId }: { tournamentId: number }) {
  const { user } = useAuth();
  const { data: tournament, isLoading } = useTournament(tournamentId);
  const register = useRegisterTournament();
  const unregister = useUnregisterTournament();

  if (isLoading || !tournament) {
    return <SheetLoading />;
  }

  const full = tournament.registered_count >= tournament.max_participants;
  const isRegistrationOpen = tournament.status === "registration";
  const isRegistered = tournament.is_registered;
  const isDoubles = tournament.team_size === 2;
  const canRegister =
    !isDoubles && isRegistrationOpen && !full && !isRegistered && user !== null;

  const isLive =
    tournament.status === "in_progress" || tournament.status === "completed";

  const actionPending = register.isPending || unregister.isPending;
  const mutationError = (register.error || unregister.error) as Error | null;

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <TournamentStatusBadge status={tournament.status} />
      <h2 className="h2" style={{ margin: 0 }}>{tournament.name}</h2>

      {tournament.description && (
        <p className="body" style={{ color: "var(--fg2)", margin: 0 }}>
          {tournament.description}
        </p>
      )}

      <Meta>
        <MetaRow icon={<Calendar size={16} />} label="Когда">
          {formatEventDate(tournament.date_time)}
        </MetaRow>
        <MetaRow icon={<MapPin size={16} />} label="Где">
          <AddressLink
            name={tournament.spot_name}
            address={tournament.spot_address}
            lat={tournament.spot_latitude}
            lon={tournament.spot_longitude}
          />
        </MetaRow>
        <MetaRow icon={<Users size={16} />} label="Участники">
          {tournament.registered_count} из {tournament.max_participants}
        </MetaRow>
        <MetaRow icon={<Wallet size={16} />} label="Взнос">
          {formatPrice(tournament.entry_fee)}
        </MetaRow>
        {tournament.creator_username && (
          <MetaRow icon={<MessageCircle size={16} />} label="Организатор">
            <OrganizerLink username={tournament.creator_username} />
          </MetaRow>
        )}
      </Meta>

      {isDoubles && user !== null && (
        <DoublesRegistration
          tournamentId={tournamentId}
          userId={user.id}
          entryFee={tournament.entry_fee}
          isOpenForRegistration={isRegistrationOpen && !full}
        />
      )}

      {canRegister && (
        <Button
          fullWidth
          size="lg"
          onClick={() => register.mutate(tournamentId)}
          disabled={actionPending}
        >
          {`Записаться · ${formatPrice(tournament.entry_fee)}`}
        </Button>
      )}

      {!isDoubles && isRegistrationOpen && !isRegistered && full && (
        <Button fullWidth variant="secondary" disabled>
          Мест нет
        </Button>
      )}

      {!isDoubles && isRegistrationOpen && isRegistered && (
        <Button
          fullWidth
          variant="ghost"
          onClick={() => unregister.mutate(tournamentId)}
          disabled={actionPending}
        >
          Отменить запись
        </Button>
      )}

      {mutationError && <MutationError message={mutationError.message} />}

      {isLive && (
        <div style={{ marginTop: 8 }}>
          <div className="section-header" style={{ padding: "0 0 8px" }}>Сетка</div>
          <BracketView tournamentId={tournamentId} />
        </div>
      )}

      {isLive && (
        <div style={{ marginTop: 8 }}>
          <TournamentStandings tournamentId={tournamentId} />
        </div>
      )}
    </div>
  );
}

function TrainingDetail({ trainingId }: { trainingId: number }) {
  const { user } = useAuth();
  const { data: training, isLoading } = useTraining(trainingId);
  const register = useRegisterTraining();
  const unregister = useUnregisterTraining();

  if (isLoading || !training) {
    return <SheetLoading />;
  }

  const registeredCount = training.registered_count ?? 0;
  const full = registeredCount >= training.max_participants;
  const isOpen = training.status === "active";
  const isRegistered = training.is_registered ?? false;
  const canRegister = isOpen && !full && !isRegistered && user !== null;

  const actionPending = register.isPending || unregister.isPending;
  const mutationError = (register.error || unregister.error) as Error | null;

  return (
    <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <Badge kind="brand">Тренировка</Badge>
      <h2 className="h2" style={{ margin: 0 }}>{training.title}</h2>

      {training.description && (
        <p className="body" style={{ color: "var(--fg2)", margin: 0 }}>
          {training.description}
        </p>
      )}

      <Meta>
        <MetaRow icon={<Calendar size={16} />} label="Когда">
          {formatEventDate(training.date_time)} · {training.duration} мин
        </MetaRow>
        {training.spot_name && training.spot_address && (
          <MetaRow icon={<MapPin size={16} />} label="Где">
            <AddressLink
              name={training.spot_name}
              address={training.spot_address}
              lat={training.spot_latitude}
              lon={training.spot_longitude}
            />
          </MetaRow>
        )}
        <MetaRow icon={<Users size={16} />} label="Участники">
          {registeredCount} из {training.max_participants}
        </MetaRow>
        <MetaRow icon={<Wallet size={16} />} label="Цена">
          {formatPrice(training.price)}
        </MetaRow>
        {training.creator_username && (
          <MetaRow icon={<MessageCircle size={16} />} label="Тренер">
            <OrganizerLink username={training.creator_username} />
          </MetaRow>
        )}
      </Meta>

      {canRegister && (
        <Button
          fullWidth
          size="lg"
          onClick={() => register.mutate(trainingId)}
          disabled={actionPending}
        >
          {`Записаться · ${formatPrice(training.price)}`}
        </Button>
      )}

      {isOpen && !isRegistered && full && (
        <Button fullWidth variant="secondary" disabled>
          Мест нет
        </Button>
      )}

      {isOpen && isRegistered && (
        <Button
          fullWidth
          variant="ghost"
          onClick={() => unregister.mutate(trainingId)}
          disabled={actionPending}
        >
          Отменить запись
        </Button>
      )}

      {mutationError && <MutationError message={mutationError.message} />}
    </div>
  );
}

function DoublesRegistration({
  tournamentId,
  userId,
  entryFee,
  isOpenForRegistration,
}: {
  tournamentId: number;
  userId: number;
  entryFee: number;
  isOpenForRegistration: boolean;
}) {
  const { data: teams = [] } = useBracketTeams(tournamentId);
  const { data: invites = [] } = useTournamentInvites(tournamentId);
  const invite = useInvitePartner(tournamentId);
  const cancel = useCancelInvite(tournamentId);
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const confirm = useConfirm();
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [teamName, setTeamName] = useState("");

  const myTeam = teams.find(
    (t) => !t.is_bye && t.members.some((m) => m.user_id === userId),
  );
  const outgoing = invites.find((inv) => inv.inviter.id === userId);
  const incoming = invites.find((inv) => inv.invitee.id === userId);

  const busyUserIds = useMemo(() => {
    const s = new Set<number>();
    for (const t of teams) for (const m of t.members) s.add(m.user_id);
    for (const inv of invites) s.add(inv.invitee.id);
    return s;
  }, [teams, invites]);

  const excludePartnerIds = useMemo(() => {
    // Exclude self + anyone already on a team / mid-invite.
    const ids = new Set<number>(busyUserIds);
    ids.add(userId);
    return Array.from(ids);
  }, [busyUserIds, userId]);

  const submitError =
    (invite.error || accept.error || decline.error || cancel.error) as Error | null;

  // Case 1: I'm already in a complete team.
  if (myTeam && myTeam.members.length === 2) {
    return (
      <InfoBlock>
        <div className="body" style={{ fontWeight: 600 }}>
          Вы в команде: {myTeam.name}
        </div>
        <div className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
          {myTeam.members
            .map((m) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.username)
            .join(" · ")}
        </div>
      </InfoBlock>
    );
  }

  // Case 2: someone invited me — show Accept / Decline.
  if (incoming) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <InfoBlock>
          <div className="body">
            <b>{nameOf(incoming.inviter)}</b> приглашает вас в команду.
          </div>
        </InfoBlock>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            fullWidth
            onClick={() => accept.mutate(incoming.id)}
            disabled={accept.isPending}
          >
            {accept.isPending ? "…" : "Принять"}
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={() => decline.mutate(incoming.id)}
            disabled={decline.isPending}
          >
            Отклонить
          </Button>
        </div>
        {submitError && <MutationError message={submitError.message} />}
      </div>
    );
  }

  // Case 3: I've already invited someone — show pending state.
  if (outgoing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <InfoBlock>
          <div className="body">
            Ждём ответа от <b>{nameOf(outgoing.invitee)}</b>.
          </div>
          <div className="caption" style={{ color: "var(--fg2)", marginTop: 4 }}>
            Команда: {outgoing.team_name ?? "—"}
          </div>
        </InfoBlock>
        <Button
          fullWidth
          variant="ghost"
          leading={<X size={14} />}
          onClick={async () => {
            const ok = await confirm({
              title: "Отменить приглашение?",
              description: `Заявка на команду с ${nameOf(outgoing.invitee)} будет отменена.`,
              confirmLabel: "Отменить",
              cancelLabel: "Назад",
              tone: "danger",
            });
            if (ok) cancel.mutate(outgoing.id);
          }}
          disabled={cancel.isPending}
        >
          Отменить приглашение
        </Button>
        {submitError && <MutationError message={submitError.message} />}
      </div>
    );
  }

  // Case 4: nothing started yet — show the invite form.
  if (!isOpenForRegistration) {
    return (
      <InfoBlock>
        <div className="body" style={{ color: "var(--fg2)" }}>
          Запись закрыта.
        </div>
      </InfoBlock>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div className="label" style={{ color: "var(--fg2)" }}>Позвать партнёра:</div>
      <PlayerPicker
        value={partnerId}
        onChange={setPartnerId}
        excludeIds={excludePartnerIds}
        searchFn={searchPlayers}
      />
      <input
        type="text"
        value={teamName}
        onChange={(e) => setTeamName(e.target.value)}
        placeholder="Название команды (необязательно)"
        style={selectStyle}
        {...noAutofill}
      />
      <Button
        fullWidth
        size="lg"
        onClick={() =>
          partnerId != null &&
          invite.mutate({
            partner_user_id: partnerId,
            team_name: teamName.trim() || undefined,
          })
        }
        disabled={partnerId == null || invite.isPending}
      >
        {invite.isPending
          ? "Отправляем…"
          : `Пригласить · ${formatPrice(entryFee)}`}
      </Button>
      {submitError && <MutationError message={submitError.message} />}
    </div>
  );
}

function nameOf(u: TournamentInvite["inviter"]): string {
  return (
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    `игрок #${u.id}`
  );
}

function InfoBlock({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-secondary)",
        borderRadius: "var(--r-md)",
        padding: 12,
      }}
    >
      {children}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  border: "1px solid var(--separator)",
  borderRadius: "var(--r-md)",
  padding: "10px 14px",
  fontSize: 15,
  background: "var(--bg)",
  color: "var(--fg1)",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

function OrganizerLink({ username }: { username: string }) {
  const clean = username.replace(/^@/, "");
  return (
    <a
      href={`https://t.me/${clean}`}
      target="_blank"
      rel="noreferrer"
      style={{
        color: "var(--brand-orange)",
        fontWeight: 500,
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      @{clean}
      <span style={{ fontSize: 12 }}>↗</span>
    </a>
  );
}

function MutationError({ message }: { message: string }) {
  return (
    <div
      style={{
        background: "rgba(223,63,64,0.1)",
        color: "var(--fg-danger)",
        padding: "8px 12px",
        borderRadius: "var(--r-md)",
        fontSize: 13,
      }}
    >
      {message}
    </div>
  );
}

function Meta({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "var(--bg-secondary)",
        borderRadius: "var(--r-md)",
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {children}
    </div>
  );
}

function MetaRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <span style={{ color: "var(--fg3)", marginTop: 2, flex: "none" }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="label" style={{ color: "var(--fg3)" }}>{label}</div>
        <div className="body" style={{ marginTop: 2 }}>{children}</div>
      </div>
    </div>
  );
}

function SheetLoading() {
  return (
    <div style={{ padding: 32, textAlign: "center", color: "var(--fg2)" }}>
      Загрузка…
    </div>
  );
}

/** Spot name + address as a tappable link that opens Yandex Maps. */
function AddressLink({
  name,
  address,
  lat,
  lon,
}: {
  name: string;
  address: string;
  lat?: number | null;
  lon?: number | null;
}) {
  // Prefer coordinates for accurate pin; fall back to address string.
  const url =
    lat != null && lon != null
      ? `https://yandex.ru/maps/?pt=${lon},${lat}&z=16&l=map`
      : `https://yandex.ru/maps/?text=${encodeURIComponent(address)}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      style={{
        color: "inherit",
        textDecoration: "none",
        display: "block",
      }}
    >
      <span
        style={{
          color: "var(--brand-orange)",
          fontWeight: 500,
        }}
      >
        {name}
      </span>
      <div style={{ color: "var(--fg3)", fontSize: 13, marginTop: 2 }}>
        {address}{" "}
        <span style={{ color: "var(--brand-orange)", fontSize: 12 }}>↗</span>
      </div>
    </a>
  );
}
